import React, { useState } from "react";
import { useCart } from "../../context/CartContext";
import { getApiBase } from "../../lib/api";

/**
 * ShippingForm:
 * - Collects customer shipping info
 * - Calls backend /shippo/rates to fetch live shipping options from Shippo
 * - Lets user pick a rate and sends the selected rate back to parent via onShippingRateSelected
 */
export type ShippingRate = {
  object_id: string;
  provider: string;
  amount: string; // NOTE: Shippo returns amount as string
  servicelevel: {
    name: string;
  };
};

type ShippingFormProps = {
  shippingData: {
    fullName: string;
    email: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone_number: string;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Parent should store this selected rate and later send:
  // rate_object_id = rate.object_id
  // shipping_rate  = Number(rate.amount)
  // carrier        = rate.provider (optional)
  onShippingRateSelected: (rate: ShippingRate) => void;
};

const ShippingForm: React.FC<ShippingFormProps> = ({
  shippingData,
  onChange,
  onShippingRateSelected,
}) => {
  const { cart } = useCart();

  const [shippingRates, setShippingRates] = useState<ShippingRate[]>([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  const [selectedRateId, setSelectedRateId] = useState<string>("");

  // Fetch rates from backend (backend talks to Shippo)
  const handleGetShippingRates = async () => {
    setLoadingRates(true);
    setRateError(null);

    const phone = shippingData.phone_number.trim();

    if (!phone) {
      setLoadingRates(false);
      setRateError("Phone number is required.");
      return;
    }

    const address = {
      fullName: shippingData.fullName,
      address: shippingData.address,
      city: shippingData.city,
      state: shippingData.state,
      zip: shippingData.zip,
      country: "US",
      phone,
      email: shippingData.email,
    };

    const items = cart.map((item) => ({
      weight: item.weight,
      length: item.length,
      width: item.width,
      height: item.height,
      quantity: item.quantity,
    }));

    try {
      const res = await fetch(`${getApiBase()}/shippo/rates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, items }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to fetch shipping rates");
      }

      const data = await res.json();
      setShippingRates(data);
    } catch (err: any) {
      console.error("Shipping Error:", err);
      setRateError(err.message);
    } finally {
      setLoadingRates(false);
    }
  };

  // When user selects a rate, notify parent
  const handleRateSelection = (rate: ShippingRate) => {
    setSelectedRateId(rate.object_id);
    onShippingRateSelected(rate);
  };

  return (
    <div className="bg-white/80 backdrop-blur-md p-6 rounded-lg shadow-md mb-8 border border-[rgba(92,47,47,0.1)]">
      <h2 className="text-xl font-semibold mb-4 text-[#5c2f2f]">Shipping Info</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          name="fullName"
          placeholder="Full Name"
          value={shippingData.fullName}
          onChange={onChange}
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={shippingData.email}
          onChange={onChange}
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
        <input
          type="text"
          name="address"
          placeholder="Address"
          value={shippingData.address}
          onChange={onChange}
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded col-span-1 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
        <input
          type="text"
          name="city"
          placeholder="City"
          value={shippingData.city}
          onChange={onChange}
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
        <input
          type="text"
          name="state"
          placeholder="State"
          value={shippingData.state}
          onChange={onChange}
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
        <input
          type="text"
          name="zip"
          placeholder="Zip Code"
          value={shippingData.zip}
          onChange={onChange}
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
        <input
          type="text"
          name="phone_number"
          placeholder="Phone Number"
          value={shippingData.phone_number || ""}
          onChange={onChange}
          required
          className="border border-[rgba(92,47,47,0.3)] p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
        />
      </div>

      <button
        onClick={handleGetShippingRates}
        className="bg-[#a25d5d] text-white px-4 py-2 rounded hover:bg-[#8f4d4d] transition mt-4 font-semibold"
      >
        {loadingRates ? "Getting Rates..." : "Get Shipping Rates"}
      </button>

      {rateError && <p className="text-red-500 mt-2 text-sm">{rateError}</p>}

      {shippingRates.length > 0 && (
        <div className="mt-4">
          <h3 className="font-semibold mb-2 text-[#5c2f2f]">Shipping Options:</h3>
          <ul className="space-y-2">
            {shippingRates.map((rate) => (
              <li
                key={rate.object_id}
                className="border border-[rgba(92,47,47,0.2)] p-2 rounded text-sm hover:bg-[#fbe7e7]/30 transition"
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shippingRate"
                    value={rate.object_id}
                    checked={selectedRateId === rate.object_id}
                    onChange={() => handleRateSelection(rate)}
                  />
                  <span>
                    <strong>{rate.provider}</strong> — {rate.servicelevel.name}: ${rate.amount}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ShippingForm;
